import { keyframes } from '@emotion/react';
import { common } from '@mui/material/colors';
import { createTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import {
  IS_VIEW_MODE_TABLE_CLASS_NAME,
  REMOVE_ROW_CELL_CLASS_NAME,
  STICKY_ACTIONS_CELL_CLASS_NAME,
  STICKY_ACTIONS_HEADER_CELL_CLASS_NAME,
  THEMES,
} from 'constant';
import {
  bgSoft,
  bgWeak,
  dataGridHeaderGrey,
  dataGridHoverRowColor,
  dataGridSelectedRowColor,
  dataGridSelectedRowColorHover,
  greens,
  neutral,
  reds,
  warning,
} from './colors';

export const animations = {
  spin: keyframes`
    0% {
        transform: rotate(0deg)
    }
    100% {
        transform: rotate(360deg)
    }
  `,
};
const generalOptions = {
  typography: {
    fontFamily: 'Inter, "sans-serif"',
    button: {
      fontWeight: 600,
    },
    textSmMedium: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
    },
    displaySmSemibold: {
      fontSize: '30px',
      fontWeight: 600,
      lineHeight: '38px',
    },
    subHeadingXSmall: {
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '16px',
      letterSpacing: '0.48px',
    },
    labelXSmall: {
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
    },
    labelSmall: {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '20px',
    },
    labelMedium: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '24px',
    },
    labelLarge: {
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: '24px',
    },
    labelXLarge: {
      fontSize: '24px',
      fontWeight: 600,
      lineHeight: '32px',
    },
    paragraphSmall: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20px',
    },
    paragraphXSmall: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '16px',
    },
    paragraphMedium: {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '24px',
    },
    paragraphLarge: {
      fontSize: '18px',
      fontWeight: 400,
      lineHeight: '24px',
    },
    h1: {
      fontSize: '3.5rem',
    },
    h2: {
      fontSize: '3rem',
    },
    h3: {
      fontSize: '2.25rem',
    },
    h4: {
      fontSize: '2rem',
    },
    h5: {
      fontSize: '24px',
      fontWeight: 600,
      lineHeight: '32px',
    },
    'h5-old': {
      fontSize: '1.5rem',
    },
    h6: {
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: '28px',
    },
    subHeading2XSmall: {
      fontSize: '11px',
      fontWeight: 500,
      lineHeight: '12px',
      letterSpacing: '0.22px',
    },
  },
  shape: {
    borderRadius: 8,
  },
};
const themeOptions = {
  [THEMES.LIGHT]: {
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: bgWeak,
              '& fieldset': {
                borderColor: neutral[200], // Border color changes on hover because this style is applied
              },
            },
            '& fieldset': {
              borderColor: neutral[200], // Default border color matches hover state
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            '&::placeholder': {
              opacity: 0.86,
              color: '#42526e',
            },
          },
        },
      },
      MuiButtonGroup: {
        styleOverrides: {
          root: {
            boxShadow: 'none',

            '&:hover': {
              boxShadow: 'none',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            textTransform: 'capitalize',
            fontWeight: 500,
            fontSize: '1rem',
            boxShadow: 'none',

            '&:hover': {
              boxShadow: 'none',
            },
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: dataGridHeaderGrey,
              padding: '16px',
              color: '#111827',
              fontSize: '1rem',
              fontWeight: 600,
            },
            '& .MuiTableCell-root': {
              fontSize: '1rem',
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: '4px',
            marginTop: '8px',
            boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            [`&.${IS_VIEW_MODE_TABLE_CLASS_NAME}`]: {
              '& .MuiDataGrid-cell': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: dataGridHeaderGrey,
              padding: '16px',
            },
            '& .MuiDataGrid-columnHeader:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaderCheckbox': {
              padding: 0,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: '#111827',
              fontWeight: 600,
              fontSize: '1rem',
            },
            '& .MuiDataGrid-cell': {
              padding: '16px',
              fontSize: '1rem',
              '& .MuiInputBase-root': {
                borderRadius: '0',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            },
            [`& .${STICKY_ACTIONS_CELL_CLASS_NAME}, & .${STICKY_ACTIONS_HEADER_CELL_CLASS_NAME}`]: {
              position: 'sticky',
              top: 'auto',
              right: 0,
            },
            [`& .${STICKY_ACTIONS_CELL_CLASS_NAME}`]: {
              backgroundColor: common.white,
            },
            [`& .MuiDataGrid-row.Mui-selected .${STICKY_ACTIONS_CELL_CLASS_NAME}`]: {
              backgroundColor: dataGridSelectedRowColor,
            },
            [`& .MuiDataGrid-row:hover .${STICKY_ACTIONS_CELL_CLASS_NAME}`]: {
              backgroundColor: dataGridHoverRowColor,
            },
            [`& .MuiDataGrid-row.Mui-selected:hover .${STICKY_ACTIONS_CELL_CLASS_NAME}`]: {
              backgroundColor: dataGridSelectedRowColorHover,
            },
            [`& .${STICKY_ACTIONS_HEADER_CELL_CLASS_NAME}`]: {
              backgroundColor: dataGridHeaderGrey,
            },
            [`& .${REMOVE_ROW_CELL_CLASS_NAME}`]: {
              padding: 0,
            },
          },
        },
      },
    },
    palette: {
      action: {
        active: '#6b778c',
      },
      background: {
        default: common.white,
        paper: common.white,
      },
      error: {
        contrastText: common.white,
        main: '#c44d56',
      },
      mode: 'light',
      primary: {
        contrastText: common.white,
        main: '#145da0',
      },
      secondary: {
        contrastText: common.white,
        main: '#989898',
      },
      success: {
        contrastText: common.white,
        main: '#16a085',
      },
      text: {
        primary: '#172b4d',
        secondary: '#6b778c',
      },
      warning: {
        contrastText: common.white,
        main: '#F2AC7A',
      },
      draft: {
        contrastText: common.white,
        main: '#AEAFAF',
      },
    },
  },
};

const theme = createTheme(deepmerge(generalOptions, themeOptions[THEMES.LIGHT]));

export const commonBorder = '1px solid #C0C7CA';
export const commonBorderLight = `1px solid ${bgSoft}`;
export const commonBorderSecondary = '1px solid #E9EAEB';
export const commonBorderLightRed = `1px solid ${reds[100]}`;
export const commonBorderLightGreen = `1px solid ${greens[100]}`;
export const commonBorderLightOrange = `1px solid ${warning.warningBase}`;


export default theme;
